import CronGenerator from 'components/CronGenerator/CronGenerator';
import Text from 'components/Text/Text';
import ActivationScheduleInput from './components/ActivationScheduleInput';
import DeactivationScheduleInput from './components/DeactivationScheduleInput';
import React, { useContext, useEffect } from 'react';
import { ISubCategoryRestaurants } from 'types';
import {
	IEditSubCategoryContext,
	EditSubCategoryContext,
} from '../../../../../editSubCategoryState/editSubCategoryContext';
import CustomerSelect from './components/CustomerSelect';
import RestaurantsMultipleSelect from './components/RestaurantsMultipleSelect/RestaurantsMultipleSelect';
import NameSubCategoryInput from './components/NameSubCategoryInput';
import CategorySelect from './components/CategorySelect';
import RestaurantsToHideMultipleSelect from './components/RestaurantsToHideMultipleSelect/RestaurantsToHideMultipleSelect';
import RestaurantsAvailableForRepetitionSelect from './components/RestaurantsAvailableForRepetitionSelect/RestaurantsAvailableForRepetitionSelect';
import DishesList from './components/DishesList';
import BtnBlock from './components/BtnBlock';
import { Form } from 'antd';
import { getFileIdForSubmit, preventDefaultForEnterKey } from 'utils/helpers';
import style from './InputFields.module.scss';

const InputFields: React.FC = () => {
	const {
		changeTypeOfModalWindow,
		setDataForSubmit,
		changeNameOfSubCategory,
		setIDCategory,
		setFetchedCategories,
		changeLoaderStatus,
		setIDCustomer,
		setFetchedCustomers,
		setIDsRestaurant,
		setFetchedRestaurants,
		setIDsOfRestaurantsWhereToHide,
		setIDsOfRestaurantsAvailableForRepetition,
		changeActivationSchedule,
		changeDeactivationSchedule,
		initialIDsOfRestaurantsAvailableForRepetition,
		initialIDsOfRestaurantsWhereToHide,
		initialIDsSelectedRestaurant,
		initialIDSelectedCustomer,
		dishesOfSubcategory,
		subCategoryID,
		fileId,
		filePath,
		IDSelectedCustomer,
		IDsSelectedRestaurant,
		fetchedRestaurants,
		nameOfSubCategory,
		IDSelectedCategory,
		initialNameSelectedCategory,
		IDsOfRestaurantsWhereToHide,
		IDsOfRestaurantsAvailableForRepetition,
		initialIDSelectedCategory,
		activationSchedule,
		deactivationSchedule,
		translations,
		defaultCustomerLanguage,
	} = useContext<IEditSubCategoryContext>(EditSubCategoryContext);
	const [form] = Form.useForm();

	useEffect(() => {
		form.setFieldsValue({ IDSelectedCustomer });
		form.setFieldsValue({ IDsSelectedRestaurant });
		form.setFieldsValue({ nameOfSubCategory });
		// form.setFieldsValue({ IDSelectedCategory });
		form.setFieldsValue({ IDSelectedCategory: initialNameSelectedCategory }); // If they make category selection active then remove that.
		form.setFieldsValue({ IDsOfRestaurantsWhereToHide });
		form.setFieldsValue({ IDsOfRestaurantsAvailableForRepetition });
		form.setFieldsValue({ activationSchedule });
		form.setFieldsValue({ deactivationSchedule });
	}, [
		IDSelectedCustomer,
		IDsSelectedRestaurant,
		nameOfSubCategory,
		IDSelectedCategory,
		initialNameSelectedCategory,
		IDsOfRestaurantsWhereToHide,
		IDsOfRestaurantsAvailableForRepetition,
		activationSchedule,
		deactivationSchedule,
	]);

	const submit = async () => {
		const defaultTranslationIsEmpty =
			translations.find((translation) => translation.languageId === defaultCustomerLanguage?.id)?.name === '';
		if (defaultTranslationIsEmpty) {
			changeTypeOfModalWindow('defaultTranslationIsEmpty');
			return;
		}

		const finalSubCategoryRestaurants: ISubCategoryRestaurants[] = IDsSelectedRestaurant.map(
			(idSelectedRestaurant) => {
				const isHidden = IDsOfRestaurantsWhereToHide.find(
					(idRestaurantToHide) => idRestaurantToHide === idSelectedRestaurant,
				);
				const isAvailableForRepetition = IDsOfRestaurantsAvailableForRepetition.find(
					(idRestaurantAvailableForRepetition) => idRestaurantAvailableForRepetition === idSelectedRestaurant,
				);
				return {
					restaurantId: idSelectedRestaurant,
					isHidden: !!isHidden,
					isAvailableForRepetition: !!isAvailableForRepetition,
				};
			},
		);

		setDataForSubmit({
			id: subCategoryID,
			fileId: getFileIdForSubmit({ fileId, filePath }),
			customerId: IDSelectedCustomer || '',
			subCategoryRestaurants: finalSubCategoryRestaurants,
			name: nameOfSubCategory,
			categoryId: initialIDSelectedCategory,
			activationCron: activationSchedule,
			deactivationCron: deactivationSchedule,
			translations,
		});
		changeTypeOfModalWindow('toConfirmSubmit');
	};

	return (
		<div className={style.container} data-test="InputFields">
			<Form form={form} name="basic" onFinish={submit} onKeyPress={preventDefaultForEnterKey} layout="vertical">
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="mainInfoHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<NameSubCategoryInput changeNameOfSubCategory={changeNameOfSubCategory} />
					<CategorySelect
						setIDCategory={setIDCategory}
						setFetchedCategories={setFetchedCategories}
						changeLoaderStatus={changeLoaderStatus}
						IDsSelectedRestaurant={IDsSelectedRestaurant}
						initialIDSelectedCategory={initialIDSelectedCategory}
					/>
					<CustomerSelect
						setIDCustomer={setIDCustomer}
						setFetchedCustomers={setFetchedCustomers}
						initialIDSelectedCustomer={initialIDSelectedCustomer}
					/>
					<RestaurantsMultipleSelect
						setIDsRestaurant={setIDsRestaurant}
						setFetchedRestaurants={setFetchedRestaurants}
						IDSelectedCustomer={IDSelectedCustomer}
						initialIDsSelectedRestaurant={initialIDsSelectedRestaurant}
						IDsSelectedRestaurant={IDsSelectedRestaurant}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="hideSubcategoryHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<RestaurantsToHideMultipleSelect
						setIDsOfRestaurantsWhereToHide={setIDsOfRestaurantsWhereToHide}
						IDSelectedCustomer={IDSelectedCustomer}
						fetchedRestaurants={fetchedRestaurants}
						IDsSelectedRestaurant={IDsSelectedRestaurant}
						IDsOfRestaurantsWhereToHide={IDsOfRestaurantsWhereToHide}
						initialIDsOfRestaurantsWhereToHide={initialIDsOfRestaurantsWhereToHide}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="availableForRepetitionHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<RestaurantsAvailableForRepetitionSelect
						setIDsOfRestaurantsAvailableForRepetition={setIDsOfRestaurantsAvailableForRepetition}
						IDSelectedCustomer={IDSelectedCustomer}
						fetchedRestaurants={fetchedRestaurants}
						IDsSelectedRestaurant={IDsSelectedRestaurant}
						IDsOfRestaurantsAvailableForRepetition={IDsOfRestaurantsAvailableForRepetition}
						initialIDsOfRestaurantsAvailableForRepetition={initialIDsOfRestaurantsAvailableForRepetition}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="dishesHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<DishesList dishesOfSubcategory={dishesOfSubcategory} />
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="scheduleHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<div className={style.row}>
						<ActivationScheduleInput changeActivationSchedule={changeActivationSchedule} />
						<DeactivationScheduleInput changeDeactivationSchedule={changeDeactivationSchedule} />
					</div>
					<CronGenerator />
				</div>
				<BtnBlock />
			</Form>
		</div>
	);
};

export default InputFields;
